import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AlertTriangle, FileText, Plus, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const STATUS_BADGE = {
  sem_bo:        { label: "🔴 Sem B.O.",       color: "bg-red-100 text-red-700" },
  bo_registrado: { label: "🟡 B.O. registrado", color: "bg-yellow-100 text-yellow-800" },
  recuperado:    { label: "🟢 Recuperado",     color: "bg-green-100 text-green-700" },
};

export default function Extravios() {
  const [extravios, setExtravios] = useState([]);
  const [totalRisco, setTotalRisco] = useState(0);
  const [unidades, setUnidades] = useState([]);
  const [analise, setAnalise] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [novoOpen, setNovoOpen] = useState(false);
  const [form, setForm] = useState({
    unidade_id: "", data_extravio: "", valor_estimado: "",
    bo_numero: "", descricao: "",
  });

  const carregar = async () => {
    const [ext, un] = await Promise.all([
      api.get("/extravios"),
      api.get("/unidades?status=disponivel").catch(() => ({ data: [] })),
    ]);
    setExtravios(ext.data.extravios);
    setTotalRisco(ext.data.total_em_risco);
    // Adiciona também unidades locadas/oficina para escolha
    try {
      const r = await api.get("/unidades");
      setUnidades(r.data.filter((u) => u.status !== "extraviada"));
    } catch {
      setUnidades(un.data);
    }
  };
  useEffect(() => { carregar(); }, []);

  const analisar = async () => {
    setLoadingAi(true);
    try {
      const r = await api.get("/ai/analise-extravios");
      setAnalise(r.data);
    } catch (e) {
      toast.error("Falha ao analisar");
    } finally {
      setLoadingAi(false);
    }
  };

  const registrar = async () => {
    if (!form.unidade_id || !form.data_extravio || !form.valor_estimado) {
      toast.error("Equipamento, data e valor são obrigatórios");
      return;
    }
    try {
      await api.post("/extravios", {
        ...form,
        valor_estimado: parseFloat(form.valor_estimado),
        data_extravio: new Date(form.data_extravio).toISOString(),
      });
      toast.success("Extravio registrado");
      setNovoOpen(false);
      setForm({ unidade_id: "", data_extravio: "", valor_estimado: "", bo_numero: "", descricao: "" });
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const marcarRecuperado = async (e) => {
    try {
      await api.patch(`/extravios/${e.id}`, { status_recuperacao: "recuperado" });
      toast.success("Marcado como recuperado");
      carregar();
    } catch {
      toast.error("Falha");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Extraviados
          </h1>
          <p className="text-sm text-muted-foreground">
            Controle de equipamentos extraviados · B.O. · análise de padrões IA
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={analisar}
            disabled={loadingAi}
            data-testid="extravios-btn-ia"
            variant="outline"
          >
            {loadingAi ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
            Analisar padrões IA
          </Button>
          <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
            <DialogTrigger asChild>
              <Button data-testid="extravios-btn-novo" className="text-white" style={{ background: "#F97316" }}>
                <Plus size={14} className="mr-2" /> Registrar extravio
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Registrar extravio</DialogTitle>
                <DialogDescription>
                  A unidade selecionada será marcada como <strong>extraviada</strong> automaticamente.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Equipamento *</Label>
                  <Select value={form.unidade_id} onValueChange={(v) => setForm({ ...form, unidade_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="extravios-form-unidade">
                      <SelectValue placeholder="Selecione um equipamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {unidades.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.numero_serie} · {u.status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Data do extravio *</Label>
                    <Input
                      type="datetime-local"
                      data-testid="extravios-form-data"
                      value={form.data_extravio}
                      onChange={(e) => setForm({ ...form, data_extravio: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Valor estimado (R$) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="extravios-form-valor"
                      value={form.valor_estimado}
                      onChange={(e) => setForm({ ...form, valor_estimado: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Número do B.O. (opcional)</Label>
                  <Input
                    data-testid="extravios-form-bo"
                    value={form.bo_numero}
                    onChange={(e) => setForm({ ...form, bo_numero: e.target.value })}
                    placeholder="Ex: 2026/123456"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea
                    data-testid="extravios-form-desc"
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <Button
                  onClick={registrar}
                  data-testid="extravios-form-salvar"
                  className="w-full text-white"
                  style={{ background: "#F97316" }}
                >
                  Registrar extravio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card
        className="p-4 border-l-4 flex items-center justify-between"
        style={{ borderLeftColor: "#dc2626" }}
        data-testid="extravios-total-risco"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle size={24} className="text-red-500" />
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Total em risco</div>
            <div className="text-2xl font-display font-bold text-red-700">{fmt(totalRisco)}</div>
          </div>
        </div>
        <div className="text-sm text-gray-600">
          {extravios.length} registro(s)
        </div>
      </Card>

      {analise && (
        <Card className="p-5 border-l-4" style={{ borderLeftColor: "#F97316" }} data-testid="extravios-analise">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-orange-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "#1B3A6B" }}>
              Análise IA
            </h2>
          </div>
          <div className="text-sm prose prose-sm max-w-none whitespace-pre-line">
            {analise.analise}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden" data-testid="extravios-lista">
        {extravios.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Nenhum extravio registrado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Equipamento</th>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Data</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3 text-left">B.O.</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {extravios.map((e) => {
                  const st = STATUS_BADGE[e.status_recuperacao] || STATUS_BADGE.sem_bo;
                  return (
                    <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`extravio-${e.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{e.modelo_nome || "—"}</div>
                        <div className="text-xs text-gray-500">{e.unidade_serie}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{e.cliente_nome || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {new Date(e.data_extravio).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-red-700">
                        {fmt(e.valor_estimado)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {e.bo_numero || <span className="text-gray-400 italic">pendente</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={st.color}>{st.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {e.status_recuperacao !== "recuperado" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => marcarRecuperado(e)}
                            data-testid={`extravio-recuperar-${e.id}`}
                            className="text-green-600 hover:text-green-700"
                          >
                            Marcar recuperado
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
